import React, { useState, useEffect, useContext, useCallback } from 'react';
import { AuthContext } from '../App';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ScrollText, Search, Filter, ChevronLeft, ChevronRight, Clock, Users, FileText, LogIn, LogOut, UserPlus, Trash2, KeyRound, RefreshCw, Upload, Settings, TrendingUp, Info, BarChart3 } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import CountUp from 'react-countup';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const API = `${window.location.origin}/api`;

const ACTION_CONFIG = {
  login: { label: 'Giriş', icon: LogIn, color: 'text-green-600', bg: 'bg-green-50' },
  logout: { label: 'Çıkış', icon: LogOut, color: 'text-gray-500', bg: 'bg-gray-50' },
  user_invite: { label: 'Kullanıcı Daveti', icon: UserPlus, color: 'text-blue-600', bg: 'bg-blue-50' },
  user_update: { label: 'Kullanıcı Güncelleme', icon: Settings, color: 'text-indigo-600', bg: 'bg-indigo-50' },
  user_delete: { label: 'Kullanıcı Silme', icon: Trash2, color: 'text-red-600', bg: 'bg-red-50' },
  role_change: { label: 'Rol Değişikliği', icon: KeyRound, color: 'text-purple-600', bg: 'bg-purple-50' },
  password_reset: { label: 'Şifre Sıfırlama', icon: RefreshCw, color: 'text-orange-600', bg: 'bg-orange-50' },
  task_create: { label: 'Görev Oluşturma', icon: FileText, color: 'text-cyan-600', bg: 'bg-cyan-50' },
  task_update: { label: 'Görev Güncelleme', icon: FileText, color: 'text-teal-600', bg: 'bg-teal-50' },
  file_upload: { label: 'Dosya Yükleme', icon: Upload, color: 'text-emerald-600', bg: 'bg-emerald-50' },
  file_delete: { label: 'Dosya Silme', icon: Trash2, color: 'text-rose-600', bg: 'bg-rose-50' },
};

const MODULE_CONFIG = {
  auth: { label: 'Kimlik Doğrulama', color: 'bg-green-100 text-green-800' },
  users: { label: 'Kullanıcılar', color: 'bg-blue-100 text-blue-800' },
  tasks: { label: 'Görevler', color: 'bg-cyan-100 text-cyan-800' },
  files: { label: 'Dosyalar', color: 'bg-purple-100 text-purple-800' },
};

const CHART_COLORS = ['#3b82f6', '#06b6d4', '#8b5cf6', '#10b981', '#f59e0b', '#ef4444', '#ec4899', '#6366f1'];

function formatDate(isoString) {
  if (!isoString) return '-';
  const d = new Date(isoString);
  return d.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' }) +
    ' ' + d.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

function formatRelativeTime(isoString) {
  if (!isoString) return '';
  const now = new Date();
  const d = new Date(isoString);
  const diffMs = now - d;
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'Az önce';
  if (diffMins < 60) return `${diffMins} dk önce`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours} saat önce`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays} gün önce`;
  return formatDate(isoString);
}

export default function AuditLog() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();

  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState(null);
  const [filters, setFilters] = useState(null);

  const [selectedAction, setSelectedAction] = useState('all');
  const [selectedModule, setSelectedModule] = useState('all');
  const [selectedUser, setSelectedUser] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    if (!user || !['admin', 'proje_yoneticisi'].includes(user.role)) {
      navigate('/dashboard');
      return;
    }
  }, [user, navigate]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ page, limit: 50 });
      if (selectedAction !== 'all') params.append('action', selectedAction);
      if (selectedModule !== 'all') params.append('module', selectedModule);
      if (selectedUser !== 'all') params.append('user_id', selectedUser);
      if (debouncedSearch) params.append('search', debouncedSearch);

      const res = await axios.get(`${API}/audit-logs?${params}`);
      setLogs(res.data.logs);
      setTotal(res.data.total);
      setTotalPages(res.data.totalPages);
      setStats(res.data.stats);
    } catch (err) {
      toast.error('Loglar yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [page, selectedAction, selectedModule, selectedUser, debouncedSearch]);

  const fetchFilters = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/audit-logs/filters`);
      setFilters(res.data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);
  useEffect(() => { fetchFilters(); }, [fetchFilters]);
  useEffect(() => { setPage(1); }, [selectedAction, selectedModule, selectedUser, debouncedSearch]);

  if (!user || !['admin', 'proje_yoneticisi'].includes(user.role)) return null;

  if (loading && logs.length === 0) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-cyan-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Aktivite günlüğü yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="audit-log-page" className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 sm:p-6 lg:p-8 space-y-6">
      {/* Header - Reports sayfasıyla aynı yapı */}
      <div className="flex items-center justify-between mb-4 sm:mb-6 pt-4 pb-4 sm:pt-6 sm:pb-6 lg:pt-8 lg:pb-8">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-2 sm:gap-3">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-xl flex items-center justify-center shadow-lg">
              <ScrollText className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
            </div>
            Aktivite Günlüğü
          </h1>
          <p className="text-gray-600 mt-2 text-sm sm:text-base">Sistemdeki tüm kritik işlemlerin detaylı kaydı</p>
        </div>
        <Button
          data-testid="audit-refresh-btn"
          variant="outline"
          size="sm"
          onClick={() => { fetchLogs(); fetchFilters(); }}
          className="flex items-center gap-2"
        >
          <RefreshCw className="w-4 h-4" />
          <span className="hidden sm:inline">Yenile</span>
        </Button>
      </div>

      {/* Info Banner - Reports sayfasıyla aynı yapı */}
      <div className="bg-blue-50 border-l-4 border-blue-500 p-3 sm:p-4 rounded-lg flex items-center gap-2 sm:gap-3">
        <Info className="w-5 h-5 text-blue-500 flex-shrink-0 hidden sm:block" />
        <div className="flex-1 min-w-0">
          <p className="text-xs sm:text-sm text-blue-900 font-medium">
            Bu sayfa giriş/çıkış, kullanıcı, görev, dosya ve rol işlemlerini kayıt altına almaktadır.
          </p>
          <p className="text-xs text-blue-700 mt-0.5 sm:mt-1 hidden sm:block">
            Sadece admin ve proje yöneticileri bu sayfaya erişebilir.
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="glass rounded-2xl p-6">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="w-5 h-5 text-cyan-600" />
          <h2 className="text-lg font-semibold text-gray-900">Filtreler</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <Label>Arama</Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                data-testid="audit-search-input"
                type="text"
                placeholder="Kullanıcı, işlem, detay..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-3 h-10 border border-gray-200 rounded-md text-sm focus:ring-2 focus:ring-cyan-200 focus:border-cyan-400 outline-none bg-white"
              />
            </div>
          </div>
          <div>
            <Label>Modül</Label>
            <Select value={selectedModule} onValueChange={setSelectedModule}>
              <SelectTrigger data-testid="audit-module-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm Modüller</SelectItem>
                {filters?.modules?.map(m => (
                  <SelectItem key={m} value={m}>{MODULE_CONFIG[m]?.label || m}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>İşlem</Label>
            <Select value={selectedAction} onValueChange={setSelectedAction}>
              <SelectTrigger data-testid="audit-action-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm İşlemler</SelectItem>
                {filters?.actions?.map(a => (
                  <SelectItem key={a} value={a}>{ACTION_CONFIG[a]?.label || a}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Kullanıcı</Label>
            <Select value={selectedUser} onValueChange={setSelectedUser}>
              <SelectTrigger data-testid="audit-user-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm Kullanıcılar</SelectItem>
                {filters?.users?.map(u => (
                  <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="glass rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-6">
            <TrendingUp className="w-6 h-6 text-cyan-600" />
            <h2 className="text-lg font-semibold text-gray-900">Genel Bakış</h2>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            <div className="p-4 sm:p-5 rounded-2xl border-2 border-indigo-200" style={{ backgroundColor: '#6366f115' }}>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-4 h-4 rounded-full bg-indigo-500" />
                <span className="font-bold text-base text-gray-900">Toplam Kayıt</span>
              </div>
              <div className="text-3xl sm:text-4xl font-bold text-gray-900"><CountUp end={stats.totalLogs} duration={1.5} separator="." /></div>
            </div>
            <div className="p-4 sm:p-5 rounded-2xl border-2 border-green-200" style={{ backgroundColor: '#22c55e15' }}>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-4 h-4 rounded-full bg-green-500" />
                <span className="font-bold text-base text-gray-900">Bugün</span>
              </div>
              <div className="text-3xl sm:text-4xl font-bold text-gray-900"><CountUp end={stats.todayCount} duration={1.5} separator="." /></div>
            </div>
            <div className="p-4 sm:p-5 rounded-2xl border-2 border-blue-200" style={{ backgroundColor: '#3b82f615' }}>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-4 h-4 rounded-full bg-blue-500" />
                <span className="font-bold text-base text-gray-900">Modüller</span>
              </div>
              <div className="text-3xl sm:text-4xl font-bold text-gray-900"><CountUp end={Object.keys(stats.moduleBreakdown).length} duration={1.5} /></div>
            </div>
            <div className="p-4 sm:p-5 rounded-2xl border-2 border-purple-200" style={{ backgroundColor: '#a855f715' }}>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-4 h-4 rounded-full bg-purple-500" />
                <span className="font-bold text-base text-gray-900">İşlem Türleri</span>
              </div>
              <div className="text-3xl sm:text-4xl font-bold text-gray-900"><CountUp end={Object.keys(stats.topActions).length} duration={1.5} /></div>
            </div>
          </div>
        </div>
      )}

      {/* Grafikler */}
      {stats && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Günlük Trend - Tam genişlik */}
          <div className="glass rounded-2xl p-6 lg:col-span-2">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-blue-600" />
              Günlük Aktivite Trendi
            </h3>
            {stats.dailyTrend && stats.dailyTrend.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={stats.dailyTrend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis
                    dataKey="_id"
                    tick={{ fontSize: 12 }}
                    tickFormatter={(v) => { const p = v.split('-'); return p.length === 3 ? `${p[2]}/${p[1]}` : v; }}
                  />
                  <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                  <Tooltip
                    contentStyle={{ backgroundColor: 'rgba(255,255,255,0.95)', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                    labelFormatter={(v) => { const p = v.split('-'); return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : v; }}
                  />
                  <Line type="monotone" dataKey="count" stroke="#3b82f6" strokeWidth={2} dot={{ fill: '#3b82f6', r: 4 }} name="İşlem Sayısı" animationDuration={1500} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex flex-col items-center justify-center h-[250px] text-gray-400">
                <TrendingUp className="w-16 h-16 mb-3 opacity-30" />
                <p className="text-sm">Henüz trend verisi yok</p>
              </div>
            )}
          </div>

          {/* Modül Dağılımı - Pie Chart */}
          <div className="glass rounded-2xl p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-cyan-600" />
              Modül Dağılımı
            </h3>
            {stats.moduleBreakdown && Object.keys(stats.moduleBreakdown).length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={Object.entries(stats.moduleBreakdown).map(([key, val]) => ({ name: MODULE_CONFIG[key]?.label || key, value: val }))}
                    cx="50%"
                    cy="50%"
                    labelLine={true}
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    outerRadius={90}
                    dataKey="value"
                    animationDuration={1500}
                    animationBegin={200}
                  >
                    {Object.keys(stats.moduleBreakdown).map((_, i) => (
                      <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ backgroundColor: 'rgba(255,255,255,0.95)', border: '1px solid #e5e7eb', borderRadius: '8px' }} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex flex-col items-center justify-center h-[250px] text-gray-400">
                <BarChart3 className="w-16 h-16 mb-3 opacity-30" />
                <p className="text-sm">Henüz modül verisi yok</p>
              </div>
            )}
          </div>

          {/* İşlem Türü Dağılımı - Bar Chart */}
          <div className="glass rounded-2xl p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <ScrollText className="w-5 h-5 text-purple-600" />
              İşlem Türü Dağılımı
            </h3>
            {stats.topActions && Object.keys(stats.topActions).length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={Object.entries(stats.topActions).map(([key, val]) => ({ name: ACTION_CONFIG[key]?.label || key, count: val }))}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} angle={-30} textAnchor="end" height={70} />
                  <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                  <Tooltip contentStyle={{ backgroundColor: 'rgba(255,255,255,0.95)', border: '1px solid #e5e7eb', borderRadius: '8px' }} />
                  <Bar dataKey="count" fill="#8b5cf6" name="Adet" animationDuration={1500} animationBegin={400} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex flex-col items-center justify-center h-[250px] text-gray-400">
                <ScrollText className="w-16 h-16 mb-3 opacity-30" />
                <p className="text-sm">Henüz işlem verisi yok</p>
              </div>
            )}
          </div>

          {/* Saatlik Dağılım */}
          <div className="glass rounded-2xl p-6 lg:col-span-2">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Clock className="w-5 h-5 text-orange-600" />
              Saatlik Aktivite Dağılımı
            </h3>
            {stats.hourlyDistribution && stats.hourlyDistribution.length > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={stats.hourlyDistribution}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="_id" tick={{ fontSize: 12 }} tickFormatter={(v) => `${v}:00`} />
                  <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                  <Tooltip
                    contentStyle={{ backgroundColor: 'rgba(255,255,255,0.95)', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                    labelFormatter={(v) => `Saat: ${v}:00`}
                  />
                  <Bar dataKey="count" fill="#f59e0b" name="İşlem Sayısı" animationDuration={1500} animationBegin={600} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex flex-col items-center justify-center h-[200px] text-gray-400">
                <Clock className="w-16 h-16 mb-3 opacity-30" />
                <p className="text-sm">Henüz saatlik veri yok</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Log Table */}
      {logs.length === 0 && !loading ? (
        <div className="glass rounded-2xl p-12 text-center">
          <Info className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-700 mb-2">Kayıt Bulunamadı</h3>
          <p className="text-gray-600">Seçtiğiniz filtreye uygun aktivite kaydı bulunmamaktadır.</p>
        </div>
      ) : (
        <div className="glass rounded-2xl p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-6 h-6 text-cyan-600" />
              <h2 className="text-lg font-semibold text-gray-900">Kayıtlar</h2>
              <span className="ml-2 min-w-[1.5rem] h-6 px-2 bg-gradient-to-r from-indigo-500 to-purple-500 text-white text-xs font-bold rounded-md inline-flex items-center justify-center shadow-lg">
                {total.toLocaleString('tr-TR')}
              </span>
            </div>
            {/* Pagination info */}
            {totalPages > 1 && (
              <p className="text-xs sm:text-sm text-gray-500">
                Sayfa {page} / {totalPages}
              </p>
            )}
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tarih</th>
                  <th className="px-3 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Kullanıcı</th>
                  <th className="px-3 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">İşlem</th>
                  <th className="hidden md:table-cell px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Modül</th>
                  <th className="px-3 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Detay</th>
                  <th className="hidden lg:table-cell px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">IP</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {logs.map((log, index) => {
                  const actionCfg = ACTION_CONFIG[log.action] || { label: log.action, icon: ScrollText, color: 'text-gray-600', bg: 'bg-gray-50' };
                  const moduleCfg = MODULE_CONFIG[log.module] || { label: log.module, color: 'bg-gray-100 text-gray-800' };
                  const IconComp = actionCfg.icon;
                  return (
                    <tr key={log.id} data-testid={`audit-log-row-${log.id}`} className={`hover:bg-gray-50 transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}>
                      <td className="px-3 sm:px-4 py-3 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{formatDate(log.createdAt)}</div>
                        <div className="text-xs text-gray-400 mt-0.5">{formatRelativeTime(log.createdAt)}</div>
                      </td>
                      <td className="px-3 sm:px-4 py-3 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{log.userName || '-'}</div>
                        <div className="text-xs text-gray-400 mt-0.5">{log.userRole || ''}</div>
                      </td>
                      <td className="px-3 sm:px-4 py-3 whitespace-nowrap">
                        <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium ${actionCfg.bg} ${actionCfg.color}`}>
                          <IconComp className="w-3.5 h-3.5" />
                          {actionCfg.label}
                        </div>
                      </td>
                      <td className="hidden md:table-cell px-4 py-3 whitespace-nowrap">
                        <span className={`inline-flex px-2.5 py-0.5 rounded-md text-xs font-medium ${moduleCfg.color}`}>
                          {moduleCfg.label}
                        </span>
                      </td>
                      <td className="px-3 sm:px-4 py-3">
                        <div className="text-sm text-gray-700 max-w-xs truncate" title={log.details}>
                          {log.details || '-'}
                        </div>
                      </td>
                      <td className="hidden lg:table-cell px-4 py-3 whitespace-nowrap">
                        <span className="text-xs text-gray-400 font-mono">{log.ipAddress || '-'}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination - Reports sayfasıyla aynı stil */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200">
              <p className="text-sm text-gray-500">
                Toplam {total.toLocaleString('tr-TR')} kayıt
              </p>
              <div className="flex items-center gap-2">
                <Button
                  data-testid="audit-prev-page"
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                  const pageNum = page <= 3 ? i + 1 : page - 2 + i;
                  if (pageNum > totalPages || pageNum < 1) return null;
                  return (
                    <Button
                      key={pageNum}
                      variant={pageNum === page ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setPage(pageNum)}
                      className="min-w-[2rem]"
                    >
                      {pageNum}
                    </Button>
                  );
                })}
                <Button
                  data-testid="audit-next-page"
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() => setPage(p => p + 1)}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
